import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const HeatMap = ({ data }) => {
  if (!data || !data.hourly) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Mapa de Calor - Conversas por Horário</h3>
        <p className="text-gray-500">Nenhum dado disponível</p>
      </div>
    );
  }

  const { hourly, labels, maxValue } = data;

  // Preparar dados para o gráfico
  const chartData = hourly.map((count, index) => ({
    hour: labels.hours[index],
    count: count,
    hourIndex: index
  }));

  // Função para definir cor baseada na intensidade (usando cores primárias)
  const getColor = (value) => {
    if (value === 0) return '#e5e7eb'; // gray-200
    const intensity = value / maxValue;
    if (intensity > 0.75) return '#ef4444'; // red-500
    if (intensity > 0.5) return '#f97316'; // orange-500
    if (intensity > 0.25) return '#00B4C0'; // primary
    return '#93C462'; // ivox green
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">Conversas por Hora do Dia</h3>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="hour"
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
            style={{ fontSize: '12px' }}
            tick={{ fill: '#6b7280' }}
          />
          <YAxis
            label={{ value: 'Número de Conversas', angle: -90, position: 'insideLeft', fill: '#6b7280' }}
            tick={{ fill: '#6b7280' }}
          />
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                return (
                  <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
                    <p className="font-semibold text-gray-800">{payload[0].payload.hour}</p>
                    <p className="text-sm text-gray-600">
                      Conversas: <span className="font-bold text-primary-600">{payload[0].value}</span>
                    </p>
                  </div>
                );
              }
              return null;
            }}
          />
          <Bar dataKey="count" radius={[4, 4, 0, 0]}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getColor(entry.count)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Estatísticas resumidas */}
      <div className="mt-6 grid grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-primary-50 to-white p-4 rounded-xl border border-primary-100">
          <p className="text-sm text-gray-600 font-medium">Total de Conversas</p>
          <p className="text-2xl font-bold text-primary-600">
            {hourly.reduce((sum, count) => sum + count, 0)}
          </p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-white p-4 rounded-xl border border-green-100">
          <p className="text-sm text-gray-600 font-medium">Horário de Pico</p>
          <p className="text-2xl font-bold text-green-600">
            {labels.hours[hourly.indexOf(Math.max(...hourly))]}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {Math.max(...hourly)} conversas
          </p>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-white p-4 rounded-xl border border-orange-100">
          <p className="text-sm text-gray-600 font-medium">Média por Hora</p>
          <p className="text-2xl font-bold text-orange-600">
            {(hourly.reduce((sum, count) => sum + count, 0) / hourly.filter(h => h > 0).length).toFixed(1)}
          </p>
        </div>
      </div>

      {/* Legenda de cores */}
      <div className="mt-4 flex items-center justify-center space-x-4 text-sm">
        <span className="text-gray-600 font-medium">Intensidade:</span>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-1">
            <div className="w-5 h-5 bg-gray-200 rounded"></div>
            <span className="text-xs text-gray-600">Baixa</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-5 h-5 rounded" style={{ backgroundColor: '#93C462' }}></div>
            <span className="text-xs text-gray-600">Média</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-5 h-5 rounded" style={{ backgroundColor: '#00B4C0' }}></div>
            <span className="text-xs text-gray-600">Alta</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-5 h-5 bg-orange-500 rounded"></div>
            <span className="text-xs text-gray-600">Muito Alta</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-5 h-5 bg-red-500 rounded"></div>
            <span className="text-xs text-gray-600">Crítica</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeatMap;
